import type { FormInstance } from '@arco-design/web-react/es/Form';
import get from 'lodash/get';

/** 公共环境变量表单行 */
export interface MaasCommonEnvFormItem {
  name: string;
  value: string;
  _self?: string;
  description?: string;
}

function normalizeCommonEnvItems(raw: unknown): MaasCommonEnvFormItem[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  const items = raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as { name?: unknown; key?: unknown; value?: unknown; _self?: unknown; description?: unknown };
      const name = String(record.name ?? record.key ?? '').trim();
      if (!name) return null;
      return {
        name,
        value: String(record.value ?? ''),
        ...(record._self != null && String(record._self).trim() ? { _self: String(record._self) } : {}),
        ...(record.description != null && String(record.description).trim() ? { description: String(record.description) } : {}),
      };
    })
    .filter(Boolean) as MaasCommonEnvFormItem[];
  return items.length ? items : undefined;
}

/** 从扁平 form store 键（commonEnv[0].name）重建 Form.List 值 */
function reconstructCommonEnvFromFlatStore(store: Record<string, unknown>): MaasCommonEnvFormItem[] | undefined {
  const indexSet = new Set<number>();
  Object.keys(store).forEach((key) => {
    const match = key.match(/^commonEnv\[(\d+)\]\./);
    if (match) indexSet.add(Number(match[1]));
  });
  if (!indexSet.size) return undefined;

  const items = [...indexSet]
    .sort((a, b) => a - b)
    .map((index) => ({
      name: String(store[`commonEnv[${index}].name`] ?? '').trim(),
      value: String(store[`commonEnv[${index}].value`] ?? ''),
      ...(store[`commonEnv[${index}]._self`] != null ? { _self: String(store[`commonEnv[${index}]._self`]) } : {}),
    }))
    .filter((item) => item.name);

  return items.length ? items : undefined;
}

/** 从 values 对象合并 commonEnv 与 environment.env */
export function resolveCommonEnvFromValues(values: {
  commonEnv?: MaasCommonEnvFormItem[];
  environment?: { env?: MaasCommonEnvFormItem[] };
}): MaasCommonEnvFormItem[] | undefined {
  // commonEnv 显式为数组（含空数组）时以之为准，避免删空后回退到编辑回填残留的 environment.env
  if (Array.isArray(values.commonEnv)) {
    return normalizeCommonEnvItems(values.commonEnv);
  }
  return normalizeCommonEnvItems(values.environment?.env);
}

/**
 * 从 Form 读取公共环境变量。
 * 分步表单隐藏前序步骤时，Form.List 可能仅以扁平键存于 store，需多路径合并。
 */
export function readCommonEnvFromForm(form: FormInstance): MaasCommonEnvFormItem[] | undefined {
  const store = form.getFields() as Record<string, unknown>;
  const environment = form.getFieldValue('environment') ?? store.environment;

  // 优先读取 commonEnv；显式空数组表示用户已清空，不再回退 environment.env
  const commonEnvCandidates = [form.getFieldValue('commonEnv'), store.commonEnv];
  for (const raw of commonEnvCandidates) {
    if (Array.isArray(raw)) {
      return normalizeCommonEnvItems(raw);
    }
  }

  const candidates = [
    get(environment, 'env'),
    form.getFieldValue('environment.env'),
    get(store, 'environment.env'),
    reconstructCommonEnvFromFlatStore(store),
  ];

  for (const raw of candidates) {
    const normalized = normalizeCommonEnvItems(raw);
    if (normalized) return normalized;
  }

  return resolveCommonEnvFromValues({
    commonEnv: store.commonEnv as MaasCommonEnvFormItem[] | undefined,
    environment: environment as { env?: MaasCommonEnvFormItem[] } | undefined,
  });
}
